import { signOut } from "next-auth/react";
import { API_URL } from "./constants";
import { TraceStore } from "./trace-store";

const handleUnauthorized = async () => {
  // If running on the server, just throw — we can't redirect from here.
  if (typeof window === "undefined") {
    throw new Error("Unauthorized: Server-side request");
  }

  // Preserve the current location so the user can be redirected back after login.
  const currentPath = window.location.pathname + window.location.search;
  const loginWithCallback = `/login?callbackUrl=${encodeURIComponent(currentPath)}`;

  await signOut({ callbackUrl: loginWithCallback });
  throw new Error("Unauthorized: Session cleared and redirected to login.");
};

const buildErrorMessage = (
  endpoint: string,
  response: Response,
  responseText: string,
  method: string = "GET",
) => {
  let errorMessage = `A apărut o eroare neașteptată. Te rugăm să încerci din nou.`;

  try {
    const errorData = JSON.parse(responseText);
    if (errorData.message) {
      return errorData.message;
    } else {
      errorMessage += `: ${responseText}`;
    }
  } catch {
    // If not JSON, use the full text
    if (responseText) {
      errorMessage += `: ${responseText}`;
    }
  }

  return errorMessage;
};

export const getEndpointUrl = (endpoint: string): string => {
  const isServer = typeof window === "undefined";
  const requestUrl = isServer ? `${API_URL}${endpoint}` : `/api${endpoint}`;
  return requestUrl;
};

export const fetchApiBase = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> => {
  const isServer = typeof window === "undefined";

  // Detect whether the caller specified Next.js `next.revalidate` so we
  // can allow caching. When `revalidate` is present we must NOT forward
  // cookies and must NOT force `cache: "no-store"`.
  const hasRevalidate = !!options?.next?.revalidate;

  let cookieHeader = undefined as string | undefined;

  const url = getEndpointUrl(endpoint);

  // Only read server cookies when we are *not* explicitly requesting a
  // cached response via `next.revalidate`.
  if (isServer && !hasRevalidate) {
    const { cookies } = await import("next/headers");
    const cookieStore = cookies();
    const allCookies = (await cookieStore).getAll();
    cookieHeader = allCookies
      .map((cookie) => `${cookie.name}=${cookie.value}`)
      .join("; ");
  }

  // If we have a recent trace id on the client, forward it to the backend
  const clientTraceId = !isServer ? TraceStore.getValidId() : null;

  let startedAt = Date.now();

  // Do not forcibly set `Content-Type: application/json` — when sending
  // FormData or Blob the browser must set the boundary automatically.
  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const isBlob = typeof Blob !== "undefined" && options.body instanceof Blob;

  const headers: HeadersInit = {
    ...(cookieHeader ? { Cookie: cookieHeader } : {}),
    ...(clientTraceId ? { "x-trace-id": clientTraceId } : {}),
    ...(options.headers || {}),
  } as HeadersInit;

  // Only default to application/json when no body type that requires
  // a browser-managed Content-Type (FormData/Blob) is present and the
  // caller hasn't already provided a Content-Type header.
  const hasContentType = Object.keys(headers).some(
    (k) => k.toLowerCase() === "content-type",
  );
  if (!hasContentType && !isFormData && !isBlob) {
    (headers as Record<string, string>)["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    cache: hasRevalidate ? "default" : "no-store",
    credentials: hasRevalidate ? "omit" : "include",
    ...options,
    headers,
  });

  // Capture the ID from the response for the next potential request
  const responseTraceId = response.headers.get("x-trace-id");
  if (!isServer) TraceStore.save(responseTraceId);

  if (process.env.NODE_ENV === "development" && isServer) {
    const urlPath = new URL(url).pathname + new URL(url).search;
    console.log(
      `${options?.method || "GET"} ${urlPath} (took ${Date.now() - startedAt}ms)`,
    );
  }

  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorized();
    }

    const responseText = await response.text();
    const errorMessage = buildErrorMessage(
      endpoint,
      response,
      responseText,
      options?.method,
    );
    const error = new Error(errorMessage);
    (error as any).isApiError = true;
    (error as any).traceId = responseTraceId;
    throw error;
  }

  return response;
};

export const fetchApiBlob = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<Blob> => {
  const response = await fetchApiBase(endpoint, options);
  return response.blob();
};

export const fetchApi = async <T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> => {
  if (
    process.env.NODE_ENV === "development" &&
    options?.method &&
    ["POST", "PUT", "PATCH"].includes(options.method.toUpperCase())
  ) {
    console.log(
      `API ${options.method.toUpperCase()} request to ${endpoint} with body:`,
      options.body,
    );
  }

  const response = await fetchApiBase(endpoint, options);
  if (response.status === 204) {
    // No content
    return undefined as unknown as T;
  }

  return response.json() as Promise<T>;
};
